
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { cleanupAuthState } from '@/utils/authUtils';
import { toast } from 'sonner';
import { logMount } from '@/components/dashboard/enhanced-logging';

// Type definition to ensure proper usage throughout the app
type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any; data?: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  refreshSession: () => Promise<void>;
};

// Create context with undefined initial value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const unmountingRef = useRef(false);
  const initializedRef = useRef(false);
  const checkingAdminRef = useRef(false);

  // Development mode logging
  useEffect(() => {
    // Use the enhanced logging utility
    const cleanup = process.env.NODE_ENV !== 'production' 
      ? logMount('AuthProvider', { path: window.location.pathname }) 
      : () => {};
    
    // Critical paths that should bypass loading state
    const bypassLoading = ['/pricing', '/', '/signin', '/signup'];
    if (bypassLoading.includes(window.location.pathname)) {
      setLoading(false);
    }
    
    // Safety timeout to prevent infinite loading
    const safetyTimer = setTimeout(() => {
      if (loading) {
        console.log('Safety timeout reached, forcing loading=false');
        setLoading(false);
      }
    }, 3000);
    
    return () => {
      unmountingRef.current = true;
      clearTimeout(safetyTimer);
      cleanup();
    };
  }, [loading]);

  // Admin status check - simplified to prevent deadlocks
  useEffect(() => {
    // Skip for non-critical pages
    if (window.location.pathname === '/pricing' || !user || checkingAdminRef.current) {
      return;
    }
    
    const checkAdminStatus = async () => {
      if (unmountingRef.current) return;
      
      try {
        checkingAdminRef.current = true;
        
        // Call the Supabase function to check admin status
        const { data, error } = await supabase.rpc('is_admin', {
          user_id: user.id
        });
        
        if (error) {
          console.error('Error checking admin status:', error);
          return;
        }
        
        if (!unmountingRef.current) {
          setIsAdmin(!!data);
        }
      } catch (error) {
        console.error('Error in admin check:', error);
      } finally {
        checkingAdminRef.current = false;
      }
    };
    
    // Small delay to prevent potential Supabase client deadlock
    setTimeout(checkAdminStatus, 100);
  }, [user]);

  // Auth state management
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    console.log("Starting auth state listener setup");
    
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (unmountingRef.current) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      console.log("Auth state changed:", _event, !!session);
    });
    
    // Then check for existing session
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth session error:', error);
          cleanupAuthState();
          setLoading(false);
          return;
        }
        
        if (unmountingRef.current) return;
        
        setSession(session);
        setUser(session?.user ?? null);

        // Check subscription status if session exists
        if (session?.user) {
          try {
            const { data } = await supabase.functions.invoke('check-subscription', {
              body: { email: session.user.email }
            });
            
            // Cache subscription status
            if (data && !unmountingRef.current) {
              localStorage.setItem('subscription_status', JSON.stringify({
                tier: data.subscription_tier || 'free',
                subscribed: !!data.subscribed,
                lastChecked: Date.now()
              }));
            }
          } catch (error) {
            console.error("Error checking subscription on login:", error);
          }
        }
      } catch (error) {
        console.error('Auth session error:', error);
        cleanupAuthState();
      } finally {
        if (!unmountingRef.current) {
          setLoading(false);
        }
      }
    };

    getSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in function with clean state handling
  const signIn = async (email: string, password: string) => {
    try {
      // Clean up any existing auth state
      cleanupAuthState();
      
      // Try to sign out first for clean slate
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (signOutErr) {
        // Ignore sign out errors
      }
      
      // Attempt sign in
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      // Provide user-friendly error message for schema errors
      if (error && (
        error.message.includes('querying schema') || 
        error.message.includes('converting NULL to string')
      )) {
        return { 
          error: { 
            message: 'Sign-in service is temporarily unavailable. Please try again later.' 
          } 
        };
      }
      
      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  // Sign up function with toast notification
  const signUp = async (email: string, password: string, metadata = {}) => {
    try {
      cleanupAuthState();
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });

      if (!error) {
        toast({
          title: "Success!",
          description: "Please check your email to confirm your account.",
        });
      }

      return { data, error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  // Sign out with complete cleanup
  const signOut = async () => {
    try {
      // Clean up auth state first
      cleanupAuthState();
      
      // Perform the signout
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear user state
      if (!unmountingRef.current) {
        setUser(null);
        setSession(null);
      }
      
      // Force reload for clean state
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error };
    }
  };

  // Session refresh with subscription update
  const refreshSession = async () => {
    try {
      // Refresh auth session
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        return;
      }
      
      if (unmountingRef.current) return;
      
      // Update state with fresh data
      if (data?.session) {
        setSession(data.session);
        setUser(data.session.user ?? null);
        
        // Refresh subscription data
        if (data.session.user?.email) {
          try {
            const result = await supabase.functions.invoke('check-subscription', {
              body: { email: data.session.user.email }
            });
            
            if (unmountingRef.current) return;
            
            // Cache subscription status
            if (result.data) {
              const newStatus = {
                tier: result.data.subscription_tier || 'free',
                subscribed: !!result.data.subscribed,
                lastChecked: Date.now()
              };
              
              localStorage.setItem('subscription_status', JSON.stringify(newStatus));
              
              // Check for status change
              try {
                const prevStatus = localStorage.getItem('previous_subscription_status');
                if (prevStatus && result.data?.subscribed) {
                  const parsedPrev = JSON.parse(prevStatus);
                  if (!parsedPrev.subscribed) {
                    toast({
                      title: "Subscription Activated",
                      description: `Your ${result.data.subscription_tier} subscription is now active!`,
                      duration: 5000,
                    });
                  }
                }
                
                // Store current status for comparison
                localStorage.setItem('previous_subscription_status', JSON.stringify({
                  subscribed: !!result.data?.subscribed,
                  tier: result.data?.subscription_tier || 'free'
                }));
              } catch (err) {
                // Ignore storage errors
              }
            }
          } catch (subError) {
            console.error("Error refreshing subscription data:", subError);
          }
        }
      }
    } catch (error) {
      console.error('Error in refreshSession:', error);
    }
  };

  // Combine auth context value
  const contextValue: AuthContextType = {
    session,
    user,
    loading,
    isLoading: loading, // Alias for component compatibility
    isAdmin,
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
